<template>
    <div class="knowledge-base-view">
        <div class="my-4 mx-auto" style="max-width: 700px;">
            <b-form @submit.prevent="onSubmitSearch" class="w-100">
                <b-input-group size="lg">
                    <b-form-input
                        v-model="searchQuery"
                        :placeholder="$t('common.search_placeholder')"
                        style="padding: 0.5rem 1rem;"
                    ></b-form-input>
                    <b-input-group-append>
                        <b-button type="submit" variant="primary">
                            <b-icon icon="search"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form>
        </div>

        <b-breadcrumb v-if="currentSectionId" class="mb-3">
            <b-breadcrumb-item
                v-for="(item, index) in breadcrumbs"
                :key="index"
                :active="item.active"
                @click="handleBreadcrumbClick(item)"
            >
                {{ item.text }}
            </b-breadcrumb-item>
        </b-breadcrumb>

        <div v-if="!currentSectionId">
            <!-- Topics view code -->
            <div class="d-flex justify-content-end align-items-center mb-3">
                <b-button variant="primary" @click="showModal(null)" v-if="isAdmin">Add Topic</b-button>
            </div>
            <div class="row">
                <draggable :disabled="!isAdmin" v-model="sections" group="sections" @end="onDragEnd" class="w-100 d-flex flex-wrap">
                    <div v-for="section in topLevelSections" :key="section.id" class="col-md-4 mb-4">
                        <b-card @click="showSection(section.id)" class="h-100 cursor-pointer text-center hover-card">
                            <b-icon v-if="section.icon" :icon="section.icon" font-scale="4" class="mb-3" :style="{ color: section.color }"></b-icon>
                            <h2 class="mb-3">
                                {{ section.title }}
                                <span v-if="section.is_hidden_for_users" class="text-muted" v-b-tooltip.hover title="This topic is only visible for admins.">
                                    <b-icon icon="eye-slash" aria-hidden="true"></b-icon>
                                </span>
                            </h2>
                            <p class="mb-3">{{ section.description}}</p>
                            <small class="text-muted">{{ $tc('common.article_count', section.posts_count || 0) }}</small>
                            <b-button v-if="isAdmin" variant="outline" @click.stop="openEditModal(section)" class="position-absolute" style="top: 10px; right: 10px;">
                                <b-icon icon="gear"></b-icon>
                            </b-button>
                        </b-card>
                    </div>
                </draggable>
            </div>
        </div>

        <div v-else>
            <!-- Current section view code -->
            <div v-if="currentSection">
                <b-card class="mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h2 class="flex-grow-1">
                            <b-icon v-if="currentSection.icon" :icon="currentSection.icon" class="mr-2" :style="{ color: currentSection.color }"></b-icon>
                            {{ currentSection.title }}
                            <span v-if="currentSection.is_hidden_for_users" class="text-muted" v-b-tooltip.hover title="This topic is only visible for admins.">
                                <b-icon icon="eye-slash" aria-hidden="true"></b-icon>
                            </span>
                        </h2>
                        <div>
                            <b-button variant="outline-primary" @click="showModal(currentSection.id)" class="mr-2" v-if="isAdmin">Add Subtopic</b-button>
                            <b-button v-if="isAdmin" variant="outline" @click="openEditModal(currentSection)">
                                <b-icon icon="gear"></b-icon> Edit
                            </b-button>
                        </div>
                    </div>
                    <p>{{ currentSection.description}}</p>
                </b-card>

                <div v-if="currentSectionSubsections.length > 0" class="mt-4">
                    <div class="row">
                        <draggable v-model="currentSectionSubsections" group="subsections" @end="onSubsectionDragEnd" class="w-100 d-flex flex-wrap">
                            <div v-for="subsection in currentSectionSubsections" :key="subsection.id" class="col-md-4 mb-2">
                                <b-card @click="showSection(subsection.id)" class="h-100 cursor-pointer text-center hover-card">
                                    <b-icon v-if="subsection.icon" :icon="subsection.icon" font-scale="4" class="mb-3" :style="{ color: subsection.color }"></b-icon>
                                    <h2 class="mb-3">
                                        {{ subsection.title }}
                                        <span v-if="subsection.is_hidden_for_users" class="text-muted" v-b-tooltip.hover title="This topic is only visible for admins.">
                                            <b-icon icon="eye-slash" aria-hidden="true"></b-icon>
                                        </span>
                                    </h2>                                    <p class="mb-3">{{ subsection.description}}</p>
                                    <small class="text-muted">{{ $tc('common.article_count', subsection.posts_count || 0) }}</small>
                                    <b-button v-if="isAdmin" variant="outline" @click.stop="openEditModal(subsection)" class="position-absolute" style="top: 10px; right: 10px;">
                                        <b-icon icon="gear"></b-icon>
                                    </b-button>
                                </b-card>
                            </div>
                        </draggable>
                    </div>
                </div>

                <div class="mt-4">
                    <b-card>
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <p class="font-weight-bold m-0">{{ $t('common.articles') }}</p>
                            <b-button variant="primary" @click="showAddPostModal" v-if="isAdmin">Add article</b-button>
                        </div>
                        <loading v-if="isLoading"></loading>
                        <template v-else>
                            <div v-if="currentSectionPosts.length === 0" class="text-center py-5">
                                <p>{{ $t('common.empty_topic') }}</p>
                            </div>
                            <div v-else>
                                <draggable
                                    v-model="currentSectionPosts"
                                    :disabled="!isAdmin"
                                    @end="handlePostsDragEnd"
                                >
                                    <div v-for="post in currentSectionPosts"
                                         :key="post.id"
                                         :id="`post-${post.id}`"
                                         class="mb-3"
                                    >
                                        <post-card
                                            :post="post"
                                            :options="postSettings"
                                            :is-admin="isAdmin"
                                            @post-deleted="handlePostDelete"
                                        ></post-card>
                                    </div>
                                </draggable>
                            </div>
                            <div v-if="hasMorePosts" class="text-center mt-3">
                                <b-button @click="loadMorePosts" variant="outline" size="sm">
                                    {{ $t('common.load_more') }}
                                </b-button>
                            </div>
                        </template>
                    </b-card>
                </div>
            </div>
            <div v-else>
                <p>Topic not found. Current Section ID: {{ currentSectionId }}</p>
            </div>
        </div>

        <add-section-modal
            :is-modal-visible="isModalVisible"
            @hide="isModalVisible = false"
            :tab-id="tabId"
            :parent-id="modalParentId"
            @section-created="handleSectionCreated"
            modal-title="Create Topic"
            input-placeholder="Getting started, Customization, Integrations..."
        />

        <edit-section-modal
            v-if="editingSection"
            :section="editingSection"
            :is-visible.sync="isEditModalVisible"
            @hide="closeEditModal"
            @update="updateSection"
            @delete="deleteSection"
        />

        <add-post-modal
            ref="addPostModal"
            :status="newStatusId"
            :section="newSectionId"
            :categories="categories"
            :is-admin="isAdmin"
            :is-admin-form="isAdmin"
            tab-type="knowledgebase"
            modal-title="Add article"
            @post-added="onPostAdded"
        ></add-post-modal>
    </div>
</template>

<script>
import AddSectionModal from './AddSectionModal.vue';
import EditSectionModal from './EditSectionModal.vue';
import PostCard from './PostCard.vue';
import Loading from './Loading.vue';
import draggable from 'vuedraggable';
import AddPostModal from './AddPostModal.vue';
import NotificationPop
    from "../mixins/NotificationPop";


export default {
    name: 'KnowledgeBaseView',
    components: {
        AddSectionModal,
        EditSectionModal,
        PostCard,
        Loading,
        draggable,
        AddPostModal
    },
    mixins: [NotificationPop],
    props: {
        tabId: {
            type: Number,
            required: true,
        },
        status: {
            type: Number,
            required: true,
        },
        tabSlug: {
            type: String,
            required: true,
        },
        isAdmin: {
            type: Boolean,
            required: true,
        },
        postSettings: {
            type: Object,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            sections: [],
            currentSectionId: null,
            currentSectionPosts: [],
            currentSectionSubsections: [],
            isLoading: false,
            hasMorePosts: false,
            isModalVisible: false,
            isEditModalVisible: false,
            modalParentId: null,
            editingSection: null,
            sectionHistory: [],
            isLoadingSection: false,
            newStatusId: null,
            newSectionId: null,
            searchQuery: '',
            postsPerPage: 15, // Pagination
            initialLoad: true,
        };
    },
    watch: {
        currentSectionId: {
            immediate: true,
            handler(newId, oldId) {
                if (newId && newId !== oldId) {
                    this.fetchPostsForSection();
                    this.fetchSubsections();
                }
            }
        }
    },
    computed: {
        topLevelSections() {
            return this.sections.filter(section => !section.parent_id);
        },
        currentSection() {
            return this.sections.find(section => section.id === this.currentSectionId);
        },
        breadcrumbs() {
            const items = [
                {
                    text: 'All Articles',
                    sectionId: null,
                    active: !this.currentSectionId
                }
            ];

            if (this.currentSection) {
                const parentSections = this.getParentSections(this.currentSection);
                items.push(...parentSections.map(section => ({
                    text: section.title,
                    sectionId: section.id,
                    active: false
                })));
                items.push({
                    text: this.currentSection.title,
                    sectionId: this.currentSection.id,
                    active: true
                });
            }

            return items;
        }
    },
    methods: {
        // Add these new methods in the methods section
        slugify(text) {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+/, '')
                .replace(/-+$/, '');
        },

        updateHash(sectionId) {
            if (sectionId) {
                const section = this.sections.find(s => s.id === sectionId);
                if (section) {
                    const slug = this.slugify(section.title);
                    window.location.hash = `${sectionId}-${slug}`;
                }
            } else {
                window.location.hash = '';
            }
        },

        parseHash() {
            const hash = window.location.hash.slice(1); // Remove the # symbol
            if (hash) {
                const sectionId = parseInt(hash.split('-')[0]);
                if (!isNaN(sectionId)) {
                    this.showSection(sectionId);
                }
            }
        },
        async handlePostsDragEnd(event) {
            // Don't process if no actual change
            if (event.oldIndex === event.newIndex) return;

            const postOrder = this.currentSectionPosts.map(post => post.id).join(',');
            const movedPost = this.currentSectionPosts[event.newIndex];

            try {
                // Update backend with new order
                await this.$http.post('/http_api/posts/reorder', {
                    post_key: movedPost.id,
                    post_order: postOrder,
                    section_id: this.currentSectionId,
                    status_id: this.status
                });

                this.popNotification('Article order updated successfully', 'success');
            } catch (error) {
                console.error('Error reordering articles:', error);
                this.popNotification('Failed to update article order. Please try again.', 'danger');

                // Revert the change in the UI
                const movedItem = this.currentSectionPosts.splice(event.newIndex, 1)[0];
                this.currentSectionPosts.splice(event.oldIndex, 0, movedItem);
            }
        },
        fetchSections() {
            this.$http.get('/http_api/sections', { params: { tab_id: this.tabId } })
                .then(response => {
                    this.sections = response.data.data;
                })
                .catch(error => {
                    console.error('Error fetching sections:', error);
                });
        },
        showModal(parentId) {
            this.modalParentId = parentId;
            this.isModalVisible = true;
        },
        openEditModal(section) {
            this.editingSection = { ...section };
            this.isEditModalVisible = true;
        },
        closeEditModal() {
            this.isEditModalVisible = false;
            this.editingSection = null;
        },
        getParentSections(section) {
            const parents = [];
            let currentSection = section;

            while (currentSection.parent_id) {
                const parentSection = this.sections.find(s => s.id === currentSection.parent_id);
                if (parentSection) {
                    parents.unshift(parentSection);
                    currentSection = parentSection;
                } else {
                    break;
                }
            }

            return parents;
        },
        updateSection(updatedSection) {
            this.$http.patch(`/http_api/sections/${updatedSection.id}`, updatedSection)
                .then(response => {
                    const updatedData = response.data.data;

                    // Update in main sections array
                    const mainIndex = this.sections.findIndex(s => s.id === updatedData.id);
                    if (mainIndex !== -1) {
                        this.$set(this.sections, mainIndex, updatedData);
                    }

                    // If it's a subsection, update in currentSectionSubsections as well
                    if (updatedData.parent_id) {
                        const subIndex = this.currentSectionSubsections.findIndex(s => s.id === updatedData.id);
                        if (subIndex !== -1) {
                            this.$set(this.currentSectionSubsections, subIndex, updatedData);
                        }
                    }

                    this.closeEditModal();
                })
                .catch(error => {
                    console.error('Error updating section:', error);
                });
        },

        deleteSection(sectionId) {
            this.$http.delete(`/http_api/sections/${sectionId}`)
                .then(() => {
                    // Remove from main sections array
                    this.sections = this.sections.filter(s => s.id !== sectionId);

                    // If it's a subsection, remove from currentSectionSubsections as well
                    this.currentSectionSubsections = this.currentSectionSubsections.filter(s => s.id !== sectionId);

                    if (this.currentSectionId === sectionId) {
                        this.currentSectionId = null;
                    }
                    this.closeEditModal();
                })
                .catch(error => {
                    console.error('Error deleting section:', error);
                });
        },
        handleSectionCreated(newSection) {
            this.sections.push(newSection);
            if (newSection.parent_id) {
                this.currentSectionSubsections.push(newSection);
            }
            this.isModalVisible = false;
        },



        handleBreadcrumbClick(item) {
            if (!item.active) {
                this.showSection(item.sectionId);
            }
        },
        showAllArticles() {
            this.currentSectionId = null;
            this.sectionHistory = [];
            this.updateHash(null);
        },

        // Update the existing showSection method
        showSection(sectionId) {
            if (sectionId === null) {
                this.showAllArticles();
            } else {
                if (this.currentSectionId) {
                    this.sectionHistory.push(this.currentSectionId);
                }
                this.currentSectionId = sectionId;
                this.currentSectionPosts = [];
                this.currentSectionSubsections = [];
                this.isLoading = true;
                if (!this.initialLoad) {  // Only update hash if not initial load
                    this.updateHash(sectionId);
                }
                this.fetchPostsForSection();
                this.fetchSubsections();
            }
        },

        fetchPostsForSection(skip = 0) {
            let queryParams = new URLSearchParams({
                tab: this.tabSlug,
                section: this.currentSectionId,
                skip: skip.toString(),
                limit: this.postsPerPage,
                order_by: 'order'
            });

            this.$http.get(`/http_api/posts?${queryParams.toString()}`)
                .then(({ data }) => {
                    if (skip === 0) {
                        this.currentSectionPosts = data.data || [];
                    } else {
                        this.currentSectionPosts = [...this.currentSectionPosts, ...(data.data || [])];
                    }
                    this.hasMorePosts = data.hasMore;
                    this.isLoading = false;
                })
                .catch(error => {
                    this.isLoading = false;
                });
        },

        fetchSubsections() {
            this.$http.get('/http_api/sections', {
                params: {
                    parent_id: this.currentSectionId,
                    tab_id: this.tabId
                }
            })
                .then(({ data }) => {
                    this.currentSectionSubsections = data.data || [];
                })
                .catch(error => {
                    console.error('Error fetching subsections:', error);
                });
        },
        onSubmitSearch() {
            const url = `/search?q=${encodeURIComponent(this.searchQuery)}&tab_id=${this.tabId}`;
            window.location.href = url;
        },
        goBack() {
            if (this.sectionHistory.length > 0) {
                this.currentSectionId = this.sectionHistory.pop();
                this.fetchPostsForSection();
                this.fetchSubsections();
            } else {
                this.currentSectionId = null;
            }
        },

        loadMorePosts() {
            this.fetchPostsForSection(this.currentSectionPosts.length);
        },
        showAddPostModal() {
            this.newStatusId = this.status
            this.newSectionId = this.currentSectionId;
            this.$refs.addPostModal.isModalVisible = true;
        },

        onPostAdded(newPost) {
            if (newPost.section_id === this.currentSectionId) {
                this.currentSectionPosts.unshift(newPost);
                if (this.currentSection) {
                    this.currentSection.posts_count = (this.currentSection.posts_count || 0) + 1;
                }
            }
            this.$emit('post-added', newPost);
        },
        onDragEnd(evt) {
            const isTopLevel = !evt.to.dataset.parentId;
            const order = isTopLevel
                ? this.sections.filter(s => !s.parent_id).map(s => s.id).join(',')
                : this.currentSectionSubsections.map(s => s.id).join(',');

            this.$http.post('/http_api/sections/reorder', {
                order: order,  // Changed from section_order to order
                tab_id: this.tabId,
                parent_id: isTopLevel ? null : this.currentSectionId
            }).then(() => {
                this.popNotification('Section order updated successfully', 'success');
            }).catch(error => {
                console.error('Error reordering sections:', error);
                this.popNotification('Failed to update section order', 'danger');
            });
        },
        onSubsectionDragEnd(evt) {
            const order = this.currentSectionSubsections.map(s => s.id).join(',');
            this.$http.post('/http_api/sections/reorder', {
                order: order,  // Changed from section_order to order
                tab_id: this.tabId,
                parent_id: this.currentSectionId
            }).then(() => {
                this.popNotification('Subsection order updated successfully', 'success');
            }).catch(error => {
                console.error('Error reordering subsections:', error);
                this.popNotification('Failed to update subsection order', 'danger');
            });
        },
        addNewPost(newPost) {
            this.currentSectionPosts.unshift(newPost);
        },
        handlePostDelete(postId) {
            // Find the deleted post object
            const deletedPost = this.currentSectionPosts.find(post => post.id === postId);

            // Emit the `post-deleted` event with the full post object
            this.$emit('post-deleted', deletedPost);

            // Optionally, remove the post from `currentSectionPosts` if necessary
            this.currentSectionPosts = this.currentSectionPosts.filter(post => post.id !== postId);

            this.popNotification('Post deleted successfully!', 'success');
        }
    },
    mounted() {
        this.fetchSections();
        this.parseHash();
        window.addEventListener('hashchange', this.parseHash);
        this.initialLoad = false;  // Set initial load to false after first load
    },
    beforeDestroy() {
        window.removeEventListener('hashchange', this.parseHash);
    },
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>